import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from '@angular/cdk/dialog';
import { KcSnackbarComponent } from '@kaspacom/ui-kit';
import { KaspaComWebWalletService } from '../../services/wallets/kaspa-com-web-wallet.service';

@Component({
  selector: 'kcc20-snackbar-top-layer',
  standalone: true,
  imports: [KcSnackbarComponent],
  templateUrl: './snackbar-top-layer.component.html',
  styleUrl: './snackbar-top-layer.component.scss',
})
export class SnackbarTopLayerComponent {
  private readonly wrapperEl = viewChild<ElementRef<HTMLElement>>('wrapperEl');
  private readonly cdkDialog = inject(Dialog);
  private readonly kaspaComWebWalletService = inject(KaspaComWebWalletService);

  /** Whether the KaspaCom web wallet iframe is currently the surface the
   * user must interact with (connect prompt, signing approval, etc). */
  private readonly isWalletSurfaceOpen = computed(() =>
    this.kaspaComWebWalletService.showWalletSignal()(),
  );

  constructor() {
    /* kc-dialog's global backdrop-filter blur (.cdk-overlay-backdrop
     * override) squashes this element into the same blurred paint layer as
     * the dialog regardless of z-index — the same issue documented at
     * length on WalletOperationTaskOverlayComponent. Promoting into the
     * browser's top layer via the Popover API (popover="manual" in the
     * template) makes it immune, since top-layer content is composited
     * after and outside normal stacking contexts entirely.
     *
     * The KaspaCom web wallet iframe is the one exception, exactly as on
     * WalletOperationTaskOverlayComponent: it is *not* top-layered, just a
     * very-high-z-index element, so normal z-index can't out-rank it once
     * this popover is promoted — it would sit above the iframe regardless.
     * kc-snackbar deliberately keeps pointer-events enabled on each toast
     * (dismiss/action/explorer-link controls), so a toast left promoted
     * could cover and eat clicks meant for a signing/connect prompt. Fully
     * demote the popover for as long as the wallet surface is open, and
     * re-promote it once the wallet closes. */
    effect(() => {
      const el = this.wrapperEl()?.nativeElement;
      if (!el) {
        return;
      }
      if (this.isWalletSurfaceOpen()) {
        if (el.matches(':popover-open')) {
          el.hidePopover();
        }
        return;
      }
      if (!el.matches(':popover-open')) {
        this.showTop(el);
      }
    });

    /* A popover's top-layer position is fixed at the moment it's shown and
     * isn't re-evaluated against backdrops that appear afterward — a dialog
     * opened after this popover was already shown still ends up blurring
     * it. Cycling the popover on every dialog open re-asserts it above that
     * new backdrop (same workaround as WalletOperationTaskOverlayComponent).
     * Skipped up front while the wallet surface is already open (the popover
     * is intentionally demoted then, see effect above); showTop() re-checks
     * the same condition when the timer actually fires, since the wallet
     * surface can open during this timeout's delay and must not be
     * re-promoted over by a stale scheduling decision. */
    this.cdkDialog.afterOpened.pipe(takeUntilDestroyed()).subscribe(() => {
      const el = this.wrapperEl()?.nativeElement;
      if (el?.matches(':popover-open') && !this.isWalletSurfaceOpen()) {
        setTimeout(() => this.showTop(el));
      }
    });
  }

  private showTop(el: HTMLElement): void {
    /* Re-checked here, not just by callers: the dialog-afterOpened path
     * schedules this via setTimeout, and the wallet surface can open in the
     * gap between that check and the timer firing. This is the single choke
     * point that actually promotes the popover, so guarding here (rather
     * than only at each call site) is what closes that race. */
    if (this.isWalletSurfaceOpen()) {
      return;
    }
    try {
      if (el.matches(':popover-open')) {
        el.hidePopover();
      }
      el.showPopover();
    } catch {
      // Ignored: benign if the browser lacks Popover API support — the
      // snackbar still renders normally in flow, just not top-layered.
    }
  }
}
